import { makeStyles } from "tss-react/mui";
import { MiBox, MiChip, MiIcon } from "@miplatform/components";
import { PLAN_CONFIG_STATUS, TYPOGRAPHY_VARIANTS } from "@miplatform/constants";
import { getCurrencyString } from "@miplatform/utils";
import { mdiChevronDown } from "@mdi/js";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Grid,
  Tooltip,
  Typography,
} from "@mui/material";

export function ImportPlanSummary({
  openMaterialResults,
  planFloors,
  sortedPlanAreas,
}) {
  const { classes, theme } = useStyles();

  const getTextColor = (material) => {
    const { planConfigStatus, removalReason } = material;
    if (planConfigStatus === PLAN_CONFIG_STATUS.REMOVED || removalReason) {
      return theme.palette.red.main;
    }
    if (planConfigStatus === PLAN_CONFIG_STATUS.ADDED) {
      return theme.palette.green.main;
    }
    return "inherit";
  };

  const getDrawColor = (value) => {
    if (value < 0) {
      return theme.palette.red.main;
    }
    if (value > 0) {
      return theme.palette.green.main;
    }
    return "inherit";
  };

  return (
    <MiBox
      className={`${classes.summaryContainer} ${
        openMaterialResults
          ? classes.summaryContainerHidden
          : classes.summaryContainerVisible
      }`}
    >
      <Box className={classes.overflowContainer}>
        {planFloors?.map((floor) => {
          const floorAreas = sortedPlanAreas?.filter(
            (area) => area.floorId === floor.id
          );

          const hasModifiedMaterials = floorAreas.some((area) =>
            area.materials?.some((material) => material.planConfigStatus)
          );

          if (!hasModifiedMaterials) {
            return null;
          }

          return (
            <Accordion
              key={floor.id}
              disableGutters
              defaultExpanded
              className={classes.modifiedMaterialsBox}
            >
              <AccordionSummary expandIcon={<MiIcon path={mdiChevronDown} />}>
                <Box className={classes.chipContainer}>
                  <MiChip
                    label="Modified"
                    size="small"
                    className={classes.modifiedChip}
                  />
                  <Typography
                    fontWeight={theme.fontWeight.semiBold}
                    variant={TYPOGRAPHY_VARIANTS.BODY2}
                  >
                    {floor.name} Plan - Summary
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Box className={classes.accordionDetailsContainer}>
                  {floorAreas.map((area, index) => {
                    const drawHeaders =
                      area.draws?.map((drawItem) => drawItem.draw) || [];

                    return (
                      <Box key={area.id} className={classes.areaContainer}>
                        {index === 0 && (
                          <Grid container justifyContent="space-between">
                            <Grid size={4} />
                            {drawHeaders.map((header) => (
                              <Grid key={header} size={2}>
                                <Typography
                                  fontWeight={theme.fontWeight.semiBold}
                                  variant={TYPOGRAPHY_VARIANTS.BODY2}
                                >
                                  {header}
                                </Typography>
                              </Grid>
                            ))}
                          </Grid>
                        )}
                        <Grid size={4}>
                          <Typography fontWeight={theme.fontWeight.semiBold}>
                            {area.label}
                          </Typography>
                        </Grid>
                        {area.materials
                          .filter((material) => material.planConfigStatus)
                          .map((material) => {
                            const itemCode =
                              material.itemCode || material.category.itemCode;
                            const itemDescription =
                              material.itemDescription ||
                              material.category.itemDescription;

                            return (
                              <Grid
                                key={material.id}
                                container
                                justifyContent="space-between"
                              >
                                <Grid size={4} />
                                <Grid size={8} />
                                <Grid size={4}>
                                  <Typography
                                    sx={{ color: getTextColor(material) }}
                                  >
                                    <Tooltip
                                      title={`${itemCode} | ${itemDescription}`}
                                      placement="left"
                                      arrow
                                    >
                                      <Typography
                                        className={classes.materialDescription}
                                        variant={TYPOGRAPHY_VARIANTS.BODY2}
                                        fontWeight={theme.fontWeight.bold}
                                      >
                                        {`${material.quantity}x ${itemCode} | ${itemDescription}`}
                                      </Typography>
                                    </Tooltip>
                                  </Typography>
                                </Grid>
                                {material.draws?.map((draw, i) => {
                                  const value = draw.value || 0;

                                  return (
                                    <Grid key={material.id + i} size={2}>
                                      <Typography
                                        variant={TYPOGRAPHY_VARIANTS.BODY2}
                                        fontWeight={theme.fontWeight.bold}
                                        sx={{ color: getDrawColor(value) }}
                                      >
                                        {value < 0
                                          ? `(${getCurrencyString(value)})`
                                          : getCurrencyString(value)}
                                      </Typography>
                                    </Grid>
                                  );
                                })}
                              </Grid>
                            );
                          })}
                      </Box>
                    );
                  })}
                </Box>
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Box>
    </MiBox>
  );
}

const useStyles = makeStyles()((theme) => ({
  summaryContainer: {
    margin: 0,
    display: "flex",
    flexDirection: "column",
    flex: 1,
  },
  overflowContainer: {
    overflow: "auto",
    height: "100%",
  },
  summaryContainerHidden: {
    display: "none",
  },
  summaryContainerVisible: {
    display: "flex",
  },
  modifiedMaterialsBox: {
    border: `2px solid ${theme.palette.teal.main}`,
    display: "flex",
    flexDirection: "column",
    marginBottom: theme.spacing(4),
    backgroundColor: theme.palette.green.light,
    "&.MuiAccordion-root": {
      borderRadius: theme.spacing(2),
    },
  },
  chipContainer: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
  },
  materialDescription: {
    textOverflow: "ellipsis",
    overflow: "hidden",
    whiteSpace: "nowrap",
    width: "100%",
  },
  modifiedChip: {
    border: `1px solid ${theme.palette.red.main}`,
    backgroundColor: theme.palette.red.light,
    borderRadius: 4,
    height: 20,
    color: theme.palette.red.main,
  },
  accordionDetailsContainer: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
  areaContainer: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
  },
}));
